import { forwardRef, type ButtonHTMLAttributes } from "react";
import { Loader2 } from "lucide-react";
import { cn } from "@/shared/lib/cn";

export interface GoogleButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  loading?: boolean;
  /** Texto del botón. Por defecto "Continuar con Google". */
  label?: string;
}

/**
 * Botón de Google.
 *
 * Es un componente aparte y no una variante de `Button` porque las
 * directrices de marca de Google fijan el logotipo a color sobre fondo
 * blanco: no es un estilo del sistema, es un activo ajeno con reglas
 * propias. Mezclarlo con las variantes obligaría a excepciones raras.
 *
 * Igual que en `Button`, el spinner NO cambia el tamaño: el contenido se
 * queda montado e invisible y el spinner se superpone.
 */
export const GoogleButton = forwardRef<HTMLButtonElement, GoogleButtonProps>(
  function GoogleButton(
    { loading = false, disabled, label = "Continuar con Google", className, ...rest },
    ref,
  ) {
    return (
      <button
        ref={ref}
        type="button"
        disabled={disabled || loading}
        aria-busy={loading || undefined}
        className={cn(
          "relative inline-flex h-12 w-full items-center justify-center gap-3 rounded-2xl",
          "border border-ds-border bg-ds-surface px-4 text-sm font-medium text-ds-ink-2",
          "transition hover:bg-ds-cream-2 hover:shadow-ds-card",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-violet-pop/50 focus-visible:ring-offset-2",
          "disabled:cursor-not-allowed disabled:opacity-60 disabled:shadow-none",
          className,
        )}
        {...rest}
      >
        <span className={cn("inline-flex items-center gap-3", loading && "invisible")}>
          <GoogleLogo />
          {label}
        </span>

        {loading && (
          <span className="absolute inset-0 inline-flex items-center justify-center">
            <Loader2
              className="h-4 w-4 animate-spin motion-reduce:animate-none"
              aria-hidden="true"
            />
            <span className="sr-only">Conectando con Google…</span>
          </span>
        )}
      </button>
    );
  },
);

/** Logotipo oficial de Google, a color. */
function GoogleLogo() {
  return (
    <svg className="h-5 w-5 shrink-0" viewBox="0 0 48 48" aria-hidden="true">
      <path
        fill="#FFC107"
        d="M43.6 20.5H42V20H24v8h11.3C33.8 31.7 29.3 35 24 35c-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C34.4 5.1 29.5 3 24 3 12.9 3 4 11.9 4 23s8.9 20 20 20c10 0 18.5-7.3 19.9-17v-5.5z"
      />
      <path
        fill="#FF3D00"
        d="M6.3 14.7l6.6 4.8C14.5 16 18.9 12.9 24 12.9c3 0 5.8 1.1 7.9 3l5.7-5.7C34.4 5.1 29.5 3 24 3 15.6 3 8.4 7.7 6.3 14.7z"
      />
      <path
        fill="#4CAF50"
        d="M24 43c5.2 0 10-2 13.6-5.3l-6.3-5.2C29.3 35 26.8 36.1 24 36.1c-5.2 0-9.5-3.3-11.2-7.9l-6.4 5C8.5 39.9 15.7 43 24 43z"
      />
      <path
        fill="#1976D2"
        d="M43.6 20.5H42V20H24v8h11.3c-1.1 3.2-3.6 5.8-6.7 7.1l.1.1 6.3 5.2c-.4.3 8-4.6 8-17.4 0-1.1-.1-2.2-.4-3.5z"
      />
    </svg>
  );
}
